import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { App } from 'ionic-angular';
import { BsDatepickerConfig } from 'ngx-bootstrap';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { Storage } from '../../../node_modules/@ionic/storage';
import { AccountHolder } from '../../app/models/AccountHolder';
import { AlertsHolder } from '../../app/models/AlertsHolder';
import { Card } from '../../app/models/Card';
import { CurrentAccount } from '../../app/models/CurrentAccount';
import { Customer } from '../../app/models/Customer';
import { User } from '../../app/models/User';
import { Config } from '../../providers/config';
import { ConfigService } from '../../providers/config/config.service';
import { NavControllerService } from '../../providers/nav-controller/navControllerService';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { Utils } from '../../providers/paytron-backend/utils';
import { TableService } from '../../services/table.service';
import { GenericQueryParams } from '../../utils/utils';
import { customerTableConfig } from './customersTableConfig';

@Component({
  selector: 'page-customers',
  templateUrl: 'customers.html',
})
export class CustomersPage implements OnDestroy, OnInit {
  @ViewChild('customerModal')
  private bootstrapModal: ModalDirective;

  @ViewChild('firstNameInput')
  firstNameInput: ElementRef;

  @ViewChild('customerForm')
  private customerForm: NgForm;

  private pageName: string = 'page-customers';
  private customers: Customer[];
  private currentCustomer: Customer = new Customer();
  private currentAccount: CurrentAccount = new CurrentAccount();
  private edit = false;
  private dateOfBirth = new Date();
  private alertsHolder: AlertsHolder;
  private cardReaderWebSocket: WebSocket;
  public tableHeaders = customerTableConfig;
  private navControllerService: NavControllerService;
  public bsConfig: Partial<BsDatepickerConfig> = new BsDatepickerConfig();
  private currentConfiguration: Config;

  private customersSubscription: Subscription;

  constructor(
    private backendService: PaytronBackendService,
    private utils: Utils,
    private app: App,
    private translate: TranslateService,
    private configService: ConfigService,
    private storage: Storage,
    public tableService: TableService
  ) {
    this.currentCustomer = new Customer();
    this.navControllerService = new NavControllerService(app, storage);
    this.alertsHolder = new AlertsHolder(translate);
    this.currentConfiguration = this.configService.getConfiguration();
  }

  ngOnInit(): void {
    this.bsConfig = this.currentConfiguration.datepickerConfig;
    this.bootstrapModal.onShown.subscribe(() => this.utils.focus(this.firstNameInput));
  }

  ionViewDidEnter() {
    this.openCardListenerWebSocket();
  }

  customersQueryFn() {
    return (params: GenericQueryParams) => this.backendService.getPaginatedCustomers(params);
  }
  submit(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.currentCustomer.dateOfBirth = this.dateOfBirth ? this.dateOfBirth.toISOString() : null;
    this.backendService
      .saveOrUpdateCustomer(this.currentCustomer)
      .subscribe(
        (data) => {
          this.tableService.refetch();
          this.alertsHolder.show('Customer successfully saved', 'success');
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        this.closeModal();
        btnEvent.disabled = false;
      });
  }

  submitAndCreateCurrentAccount(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;

    this.backendService
      .saveOrUpdateCustomer(this.currentCustomer)
      .subscribe(
        (customer) => {
          let account = new CurrentAccount();
          account.accountHolderUuid = customer.uuid;
          this.backendService.saveOrUpdateCurrentAccount(account).subscribe((currentAccount) => {
            this.currentAccount = currentAccount;
            this.showCurrentAccountDetails(this.currentAccount);
          });
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        btnEvent.disabled = false;
      });
  }

  addCustomer() {
    this.edit = false;
    this.currentCustomer = new Customer();
    this.customerForm.resetForm(this.currentCustomer);
    this.showModal();
  }

  editCustomer(customer) {
    this.edit = true;
    this.currentCustomer = customer;
    this.dateOfBirth = this.currentCustomer.dateOfBirth ? new Date(this.currentCustomer.dateOfBirth) : null;
    this.showModal();
  }

  showModal() {
    this.bootstrapModal.show();
  }

  closeModal() {
    this.bootstrapModal.hide();
  }

  showCustomerGroupPage() {
    this.navControllerService.showCustomerGroupPage();
  }

  showCustomerDetailsPage(customer: Customer) {
    this.navControllerService.showCustomerDetailsPage(customer.uuid);
  }

  showCurrentAccountDetails(currentAccount: CurrentAccount) {
    this.navControllerService.showCurrentAccountDetails(currentAccount.uuid);
  }

  private openCardListenerWebSocket() {
    if (this.cardReaderWebSocket) {
      this.cardReaderWebSocket.close();
    }
    let webSocket: WebSocket = (this.cardReaderWebSocket = new WebSocket(
      this.configService.getCardReaderWebSocketUrl()
    ));
    webSocket.addEventListener('error', (ev: Event) => {
      if (webSocket.readyState == WebSocket.CLOSED) {
        this.alertsHolder.show('Card reader not found. Set it up and retry.', 'danger');
      } else if (webSocket.readyState == WebSocket.CONNECTING) {
        this.alertsHolder.show('Card reader still connecting. Please wait.', 'danger');
      } else {
        this.alertsHolder.show('Error while reading the card.', 'danger');
        console.error('Card reader error', ev);
      }
    });
    webSocket.addEventListener('message', (ev: MessageEvent) => {
      let evData: any = JSON.parse(ev.data);
      switch (evData.messageType) {
        case 'event':
          switch (evData.message.event) {
            case 'UNPROTECTED_CARD_DETECTED':
              this.alertsHolder.show('Card unrecognized.', 'danger');
              this.sendCardReaderHaltCommand(webSocket);
              break;
            case 'CARD_DETECTED':
              let detectedCardId = evData.message.values.cardId;
              this.sendCardReaderHaltCommand(webSocket);
              this.backendService.getCard(detectedCardId).subscribe(
                (card: Card) => {
                  this.backendService.getAccountOwnerByCard(card).subscribe(
                    (owner: Customer | User) => {
                      let ownerType = card.type.service ? AccountHolder.User : AccountHolder.Customer;
                      this.redirectToAccountsOwnerDetails(owner.uuid, ownerType);
                      webSocket.close();
                    },
                    (err: HttpErrorResponse) => {
                      this.alertsHolder.show('Server error.', 'danger');
                    }
                  );
                },
                (error: any) => {
                  console.error(error);
                  this.alertsHolder.show('Server error.', 'danger');
                }
              );
              break;
          }
          break;
      }
    });
    webSocket.addEventListener('open', (ev: Event) => {
      this.sendCardReaderHaltCommand(webSocket);
    });
  }

  private sendCardReaderHaltCommand(cardReaderWebSocket: WebSocket) {
    cardReaderWebSocket.send(
      JSON.stringify({
        messageType: 'command',
        message: {
          command: 'HALT',
        },
      })
    );
  }

  hasTransactions(customer: Customer): boolean {
    let hasTransactions: boolean = false;
    for (let i: number = 0; !hasTransactions && i < customer.currentAccounts.length; i++) {
      for (let j: number = 0; !hasTransactions && j < customer.currentAccounts[i].cards.length; j++) {
        hasTransactions = customer.currentAccounts[i].cards[j].balanceVersion > 0;
        return hasTransactions;
      }
    }
  }

  private redirectToAccountsOwnerDetails(accountOwnerUuid: string, accountHolderType: AccountHolder) {
    this.navControllerService.showAccountsOwnerDetails(accountOwnerUuid, accountHolderType);
  }

  getTitle(msg: string) {
    return this.translate.instant(msg);
  }

  ngOnDestroy(): void {
    if (this.cardReaderWebSocket) {
      this.cardReaderWebSocket.close();
    }
    if (this.customersSubscription) this.customersSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/pages/customers/customers.ts